import React, { useState, useEffect, useRef } from "react";
import { usePaginationEngine } from "../letter/LetterPreview/PaginationEngine";
import { useImageToBase64 } from "../letter/utlis/useImageToBase64";
import { A4_WIDTH_PX } from "../letter/utlis/letterUtils";
import LetterPage from "../letter/LetterPreview/LetterPage";
import { normalizeFieldOrder } from "../letter/utlis/normalizeFieldOrder";

export default function LetterPreview({ state, selectedTemplate, companyProfile }) {
    const containerRef = useRef(null);
    const [scale, setScale] = useState(1);
    const [localIp, setLocalIp] = useState("");

    useEffect(() => {
        if (window.location.hostname === 'localhost') {
            const backendUrl = process.env.REACT_APP_ENTERPRISE_BACKEND_URL || "http://localhost:4040";
            fetch(`${backendUrl}/api/letterHead/local-ip`)
                .then(res => res.json())
                .then(data => {
                    if (data.localIp) {
                        setLocalIp(data.localIp);
                    }
                })
                .catch(err => console.error("Failed to fetch local IP:", err));
        }
    }, []);

    useEffect(() => {
        const handleResize = () => {
            if (containerRef.current) {
                const parentWidth = containerRef.current.offsetWidth;
                // Add padding to account for container padding
                const availableWidth = parentWidth - 40; // 24px padding on each side
                const newScale = Math.min(availableWidth / A4_WIDTH_PX, 1);
                setScale(newScale > 0 ? newScale : 1);
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getComponent = (type) => {
        const comps = selectedTemplate?.TemplateComponents || selectedTemplate?.components || [];
        return comps.find(c => c.component_type === type || c.type === type);
    };

    const getProperty = (component, key) => {
        if (!component) return null;
        if (component.properties && !Array.isArray(component.properties)) return component.properties[key];
        const props = component.TemplateProperties || component.properties;
        if (Array.isArray(props)) {
            const prop = props.find(p => p.property_key === key);
            return prop ? prop.property_value : null;
        }
        return null;
    };

    const getIdentity = (key) => {
        if (!selectedTemplate) return null;
        const identities = selectedTemplate.TemplateIdentities || selectedTemplate.template_identity_fields || selectedTemplate.identityFields || [];
        const field = identities.find(f => (f.field_key === key) || (f.key === key));
        return field ? (field.field_value || field.value) : null;
    };

    // --- Core Data Extraction ---
    const identities = selectedTemplate?.TemplateIdentities || selectedTemplate?.template_identity_fields || selectedTemplate?.identityFields || [];
    const companyName = getIdentity("companyName") || companyProfile?.name || "YOUR COMPANY";
    const companyEmail = getIdentity("companyEmail") || companyProfile?.email;
    const companyPhone = getIdentity("companyPhone") || companyProfile?.phone;
    const companyAddress = getIdentity("companyAddress") || companyProfile?.address;

    const standardKeys = ["companyName", "companyEmail", "companyPhone", "companyAddress", "cin", "gst"];
    const customIdentities = identities.filter(f => !standardKeys.includes(f.field_key || f.key));

    // Helper to safely grab any dynamic value
    const getFieldValue = (keyStr) => {
        if (keyStr === 'name' || keyStr === 'companyName') return companyName;
        if (keyStr === 'email' || keyStr === 'companyEmail') return companyEmail;
        if (keyStr === 'phone' || keyStr === 'companyPhone') return companyPhone;
        if (keyStr === 'address' || keyStr === 'companyAddress') return companyAddress;
        if (keyStr === 'cin') return getIdentity("cin");
        if (keyStr === 'gst') return getIdentity("gst");

        const field = customIdentities.find(f => (f.field_key === keyStr) || (f.key === keyStr));
        return field ? (field.field_value || field.value) : null;
    };

    // --- Header Data ---
    const headerComp = getComponent("header");
    const headerBg = getProperty(headerComp, "backgroundColor") || "#ffffff";
    const headerLogoRaw = getProperty(headerComp, "logoUrl");
    const showHeaderBg = String(getProperty(headerComp, "showBackground")) !== "false";
    const headerLayout = getProperty(headerComp, "headerLayout") || "stacked";
    const logoAlign = getProperty(headerComp, "logoAlignment") || "left";
    const textAlign = getProperty(headerComp, "textAlignment") || logoAlign || "left";
    const isStacked = headerLayout === "stacked";
    const isLogoLeft = headerLayout === "logo-left";
    const logoSizeRaw = getProperty(headerComp, "logoSize") || "medium";
    const logoSizeClass = { small: "h-12", medium: "h-16", large: "h-24" }[logoSizeRaw] || "h-16";
    const textClasses = { left: "items-start text-left", center: "items-center text-center", right: "items-end text-right" }[textAlign] || "items-start text-left";
    const logoWrapperClasses = isStacked ? { left: "justify-start", center: "justify-center", right: "justify-end" }[logoAlign] || "justify-start" : "";

    const visibleFieldsRaw = getProperty(headerComp, "visibleFields");
    let visibleFields = { name: true, email: true, phone: true, address: true };
    try {
        if (typeof visibleFieldsRaw === 'string') visibleFields = JSON.parse(visibleFieldsRaw);
        else if (typeof visibleFieldsRaw === 'object' && visibleFieldsRaw) visibleFields = visibleFieldsRaw;
    } catch (e) { }

    // 🔥 DYNAMIC HEADER ORDERING
    const baseHeaderKeys = ["name", "email", "phone", "address", ...customIdentities.map(f => f.field_key || f.key)];
    const headerOrder = normalizeFieldOrder(getProperty(headerComp, "headerFieldOrder") ?? selectedTemplate?.headerFieldOrder, baseHeaderKeys || []);

    const headerFieldsMap = headerOrder.map(key => {
        const val = getFieldValue(key);
        let label = key === "name" ? "Company" : key.charAt(0).toUpperCase() + key.slice(1);
        const custom = customIdentities.find(f => (f.field_key === key || f.key === key));
        if (custom) label = key;
        return { key, label, value: val, isVisible: visibleFields[key] !== false };
    });

    const BACKEND_URL = process.env.REACT_APP_ENTERPRISE_BACKEND_URL || "http://localhost:4040";
    const fullLogoUrl = headerLogoRaw ? (headerLogoRaw.startsWith("http") || headerLogoRaw.startsWith("data:") ? headerLogoRaw : `${BACKEND_URL}${headerLogoRaw}`) : null;
    const finalLogo = useImageToBase64(fullLogoUrl);
    const finalSignature = useImageToBase64(state.uploadedSignatureUrl);
    const finalDrawnSignature = useImageToBase64(state.drawnSignature);

    // --- Footer Data ---
    const footerComp = getComponent("footer");
    const footerBg = getProperty(footerComp, "backgroundColor") || "#ffffff";
    const footerText = getProperty(footerComp, "text");
    const enableQR = state.enableQR !== undefined ? state.enableQR : String(getProperty(footerComp, "enableQR")) === "true";
    const showCompanyInfoFooter = String(getProperty(footerComp, "showCompanyInfo")) === "true";
    const footerVisibleFieldsRaw = getProperty(footerComp, "visibleFields");

    let footerVisibleFields = { cin: true, gst: true };
    try {
        if (typeof footerVisibleFieldsRaw === 'string') footerVisibleFields = JSON.parse(footerVisibleFieldsRaw);
        else if (typeof footerVisibleFieldsRaw === 'object' && footerVisibleFieldsRaw) footerVisibleFields = footerVisibleFieldsRaw;
    } catch (e) { }

    // 🔥 DYNAMIC FOOTER ORDERING
    const baseFooterKeys = ["name", "email", "phone", "address", "cin", "gst", ...customIdentities.map(f => f.field_key || f.key)];
    const footerOrder = normalizeFieldOrder(getProperty(footerComp, "footerFieldOrder") ?? selectedTemplate?.footerFieldOrder, baseFooterKeys);

    const footerFieldsMap = footerOrder.map(key => {
        const val = getFieldValue(key);
        let label = key === "name" ? "Company" : key === "cin" ? "CIN" : key === "gst" ? "GST Number" : key.charAt(0).toUpperCase() + key.slice(1);
        const custom = customIdentities.find(f => (f.field_key === key || f.key === key));
        if (custom) label = key;
        return { key, label, value: val, isVisible: !!footerVisibleFields[key] };
    });

    const templateQrRaw = getProperty(footerComp, "qrData");
    let templateQrData = {};
    try { templateQrData = typeof templateQrRaw === 'string' ? JSON.parse(templateQrRaw) : templateQrRaw; } catch (e) { }

    const qrData = {
        iqCode: state.iqCode || templateQrData?.iqCode || "N/A",
        issuedTo: state.issuedTo || state.recipientName || templateQrData?.issuedTo || "Recipient",
        subject: state.subjectQr || state.subject || templateQrData?.subject || "Letter Subject",
        issuedBy: state.issuedBy || templateQrData?.issuedBy || companyName || "Issuing Authority"
    };

    // const getQrUrl = () => {
    //     const baseUrl = window.location.hostname === "localhost" ? 'http://192.168.1.8:4000' : window.location.origin;
    //     const params = new URLSearchParams({ code: qrData.iqCode || "", issuer: qrData.issuedBy || "" });
    //     return `${baseUrl}/verify?${params.toString()}`;
    // };

    const getQrUrl = () => {
        const hostname = window.location.hostname === "localhost" ? (localIp || 'localhost') : window.location.hostname;
        const port = window.location.port ? `:${window.location.port}` : '';
        const protocol = window.location.protocol;
        const baseUrl = `${protocol}//${hostname}${port}`;
        const params = new URLSearchParams({ code: qrData.iqCode || "", issuer: qrData.issuedBy || "" });
        return `${baseUrl}/verify?${params.toString()}`;
    };

    const isLight = (color) => {
        if (!color || color === 'transparent') return true;
        const hex = color.replace("#", "");
        const r = parseInt(hex.substring(0, 2), 16), g = parseInt(hex.substring(2, 4), 16), b = parseInt(hex.substring(4, 6), 16);
        return ((r * 299 + g * 587 + b * 114) / 1000) > 128;
    };

    const textColorClass = isLight(headerBg) ? 'text-slate-900' : 'text-white';
    const subTextColorClass = isLight(headerBg) ? 'text-slate-700' : 'text-white/80';
    const footerTextClass = isLight(footerBg) ? 'text-slate-600' : 'text-white/90';

    // Pack configurations to pass to children easily
    const headerConfig = {
        isPdfMode: true,
        headerBg,
        showHeaderBg,
        textColorClass,
        subTextColorClass,
        isStacked,
        isLogoLeft,
        logoWrapperClasses,
        textClasses,
        textAlign,
        logoSrc: finalLogo,
        logoSizeClass,
        showCompanyInfo: true,
        headerFields: headerFieldsMap, // 🔥 Pass our newly ordered map
    };

    const footerConfig = {
        isPdfMode: true,
        footerBg,
        footerTextClass,
        footerText,
        showCompanyInfoFooter,
        footerFields: footerFieldsMap, // 🔥 Pass our newly ordered map
        enableQR,
        qrUrl: getQrUrl(),
        iqCode: qrData?.iqCode
    };

    const pages = usePaginationEngine(state, isStacked, headerLayout);

    return (
        <div
            ref={containerRef}
            className="w-full h-full flex flex-col items-center bg-slate-200/50 p-6 overflow-x-hidden scrollbar-thin scrollbar-thumb-slate-300"
        >
            <style>{`
                @media print {
                    @page { size: A4; margin: 0 !important; }
                    html, body { 
                        width: 210mm; 
                        height: 297mm; 
                        margin: 0 !important; 
                        padding: 0 !important; 
                        background: white; 
                    }
                    body > *:not(.single-page-wrapper) { display: none !important; }
                    .no-print, nav, header, aside, .modal-ui { display: none !important; }
                    .single-page { 
                        width: 210mm !important; 
                        height: 297mm !important; 
                        margin: 0 !important; 
                        padding: 0 !important; 
                        box-shadow: none !important; 
                        page-break-after: always !important; 
                        break-after: page !important; 
                        transform: none !important; 
                        position: relative !important; 
                        overflow: hidden !important; 
                        left: 0 !important; 
                        top: 0 !important; 
                        border-radius: 0 !important;
                        background-color: white !important;
                    }
                    .single-page:last-child { 
                        page-break-after: avoid !important; 
                        break-after: auto !important; 
                    }
                    * { 
                        -webkit-print-color-adjust: exact !important; 
                        print-color-adjust: exact !important; 
                    }
                }
                
                p, li, .content-block {
                    page-break-inside: avoid !important;
                    break-inside: avoid !important;
                }
            `}</style>

            <div className="flex justify-between items-center w-full max-w-[500px] lg:max-w-full mb-4 pointer-events-none no-print shrink-0">
                <h3 className="text-lg font-bold text-slate-900 ">Preview</h3>
            </div>

            <div className="pb-20 print:pb-0 single-page-wrapper z-10">
                {pages?.map((chunk, index) => (
                    <LetterPage
                        key={index}
                        content={chunk}
                        pageIndex={index}
                        totalPages={pages.length}
                        scale={scale}
                        state={state}
                        headerConfig={headerConfig}
                        footerConfig={footerConfig}
                        companyName={companyName}
                        finalDrawnSignature={finalDrawnSignature}
                        finalSignature={finalSignature}
                    />
                ))}
            </div>
        </div>
    );
}
